import React, { useState } from 'react';
import { 
  Avatar, Button, Divider, Typography, Input, Space, 
  Popconfirm, Card, Tooltip, Tag, Alert, Empty
} from 'antd';
import { 
  UserOutlined, 
  DeleteOutlined, 
  SendOutlined,
  CalendarOutlined,
  MessageOutlined,
  ArrowLeftOutlined,
  PaperClipOutlined,
  DownloadOutlined
} from '@ant-design/icons';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * MessageDetail component for displaying a message and its conversation history
 */
const MessageDetail = ({ message, onReply, onDelete }) => {
  const [replyText, setReplyText] = useState('');
  const [replyVisible, setReplyVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle reply submission
  const handleReply = async () => {
    if (!replyText.trim()) return;
    
    try {
      setSubmitting(true);
      await onReply(message.id, replyText);
      setReplyText('');
      setReplyVisible(false);
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Render attachments if any
  const renderAttachments = () => {
    if (!message.attachments || message.attachments.length === 0) {
      return null;
    }

    return (
      <div className="mt-4">
        <Divider orientation="left">
          <Text type="secondary">
            <PaperClipOutlined /> Attachments ({message.attachments.length})
          </Text>
        </Divider>
        
        <div className="flex flex-wrap gap-2">
          {message.attachments.map((attachment, index) => (
            <Card 
              key={index} 
              size="small" 
              className="w-full md:w-64 bg-gray-50 hover:bg-gray-100"
            >
              <div className="flex items-center">
                <div className="mr-2">
                  {attachment.type?.includes('image') ? (
                    <img 
                      src={attachment.url} 
                      alt={attachment.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                      <PaperClipOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                    </div>
                  )}
                </div>
                <div className="flex-grow truncate">
                  <div className="truncate font-medium text-sm">
                    {attachment.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {attachment.size && (
                      <span>{(attachment.size / 1024).toFixed(2)} KB</span>
                    )}
                  </div>
                </div>
                <Tooltip title="Download">
                  <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    size="small"
                    href={attachment.url}
                    target="_blank"
                    download={attachment.name}
                  />
                </Tooltip>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // Format message thread
  const renderMessageThread = () => {
    if (!message.thread || message.thread.length === 0) {
      return null;
    }

    return (
      <div className="mt-6">
        <Divider orientation="left">
          <Text type="secondary">Previous Messages</Text>
        </Divider>
        
        {message.thread.map((threadItem, index) => (
          <div 
            key={index} 
            className={`mb-4 p-4 rounded-lg ${
              threadItem.is_from_me ? 'bg-blue-50 ml-8' : 'bg-gray-50 mr-8'
            }`}
          >
            <div className="flex items-start">
              <Avatar 
                src={threadItem.sender_avatar}
                icon={!threadItem.sender_avatar && <UserOutlined />}
                className="mr-3 mt-1"
              />
              
              <div className="flex-grow">
                <div className="flex justify-between items-center mb-1">
                  <Text strong>
                    {threadItem.is_from_me ? 'You' : (threadItem.sender_name || 'Admin')}
                  </Text>
                  <Text type="secondary" className="text-xs">
                    {formatDate(threadItem.created_at)}
                  </Text>
                </div>
                
                <Paragraph>
                  {threadItem.message}
                </Paragraph>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (!message) {
    return <Empty description="No message selected" />;
  }

  return (
    <div className="message-detail">
      {/* Message Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <Title level={4}>{message.subject || 'No Subject'}</Title>
          
          <Popconfirm
            title="Are you sure you want to delete this message?"
            onConfirm={onDelete}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </div>
        
        <div className="flex items-center gap-2 text-gray-500 mb-4">
          <CalendarOutlined />
          <span>{formatDate(message.created_at)}</span>
          
          {message.importance === 'high' && (
            <Tag color="red">Important</Tag>
          )}
        </div>
      </div>
      
      {/* Sender Info */}
      <Card className="mb-6">
        <div className="flex items-center">
          <Avatar 
            size={48}
            src={message.sender_avatar}
            icon={!message.sender_avatar && <UserOutlined />}
            className="mr-4"
          />
          
          <div>
            <div className="font-medium">{message.sender_name || 'System Message'}</div>
            {message.sender_email && (
              <div className="text-gray-500">{message.sender_email}</div>
            )}
            {message.sender_role && (
              <Tag color="blue">{message.sender_role}</Tag>
            )}
          </div>
        </div>
      </Card>
      
      {/* Message Content */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <Paragraph className="whitespace-pre-line">
          {message.message}
        </Paragraph>
        
        {renderAttachments()}
      </div>
      
      {/* Previous Messages in Thread */}
      {renderMessageThread()}
      
      {/* Reply Section */}
      <Divider />
      
      {!replyVisible ? (
        <Button 
          type="primary" 
          icon={<MessageOutlined />} 
          onClick={() => setReplyVisible(true)}
          className="bg-golden-navy"
        >
          Reply
        </Button>
      ) : (
        <div className="reply-section">
          <TextArea
            rows={4}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply here..."
            className="mb-3"
          />
          
          <div className="flex justify-end">
            <Space>
              <Button onClick={() => setReplyVisible(false)}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                icon={<SendOutlined />} 
                onClick={handleReply} 
                loading={submitting}
                disabled={!replyText.trim()}
                className="bg-golden-navy"
              >
                Send Reply
              </Button>
            </Space>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageDetail;